import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import { IProfissional } from '@nomoh-monorepo/shared-types';
import { firstValueFrom } from 'rxjs';
import { ProfissionalDataService } from './profissional.dataservice';
import { AgendaDataService } from '../feature-agenda/agenda.dataservice';
import { AgendaService } from '../feature-agenda/agenda.service';

/**
 * Service HTTP para Profissional
 */
@Injectable({
  providedIn: 'root',
})
export class ProfissionalService {
  private http = inject(HttpClient);
  private env = inject(EnvironmentService);
  private dataService = inject(ProfissionalDataService);
  private agendaDataService = inject(AgendaDataService);
  private agendaService = inject(AgendaService);

  private get baseUrl(): string {
    return `${this.env.getApiUrl('bardo')}/profissional`;
  }

  async findAll(filtro?: Record<string, unknown>): Promise<IProfissional[]> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();
      const profissionais = await firstValueFrom(
        this.http.post<IProfissional[]>(`${this.baseUrl}/findAll`, filtro || {})
      );

      this.dataService.setProfissionais(profissionais);
      return profissionais;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao buscar profissionais';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async findById(id: string): Promise<IProfissional> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      const profissional = await firstValueFrom(
        this.http.get<IProfissional>(`${this.baseUrl}/findById`, {
          params: { id },
        })
      );

      this.dataService.setProfissionalAtual(profissional);
      return profissional;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao buscar profissional';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async criar(profissional: Partial<IProfissional>): Promise<string> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      const id = await firstValueFrom(
        this.http.post<string>(`${this.baseUrl}/create`, profissional)
      );

      await this.findAll();
      return id;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao criar profissional';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async criarMultiplo(
    profissionais: Partial<IProfissional>[]
  ): Promise<IProfissional[]> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      const criados = await firstValueFrom(
        this.http.post<IProfissional[]>(
          `${this.baseUrl}/createMultiple`,
          profissionais
        )
      );

      await this.findAll();
      return criados;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao criar profissionais';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async atualizar(
    id: string,
    profissional: Partial<IProfissional>
  ): Promise<void> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      await firstValueFrom(
        this.http.put<void>(`${this.baseUrl}/update`, {
          id,
          ...profissional,
        })
      );

      this.dataService.updateProfissional(id, profissional);
      this.agendaDataService.atualizarProfissionalNosAgendamentos(id, profissional);
      this.agendaService
        .buscarAgenda()
        .catch((err) => console.error('Erro ao recarregar agenda', err));
      await this.findAll();
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao atualizar profissional';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async deletar(id: string): Promise<void> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      await firstValueFrom(
        this.http.delete<void>(`${this.baseUrl}/delete`, {
          params: { id: id.toString() },
        })
      );

      this.dataService.removeProfissional(id);
      await this.findAll();
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao deletar profissional';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async ativar(id: string): Promise<void> {
    await this.atualizar(id, { status: true });
  }

  async desativar(id: string): Promise<void> {
    await this.atualizar(id, { status: false });
  }

  async trocarVaga(reativarId: string, pausarId: string): Promise<void> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();
      await firstValueFrom(
        this.http.post<void>(`${this.baseUrl}/trocar-vaga`, {
          reativarId,
          pausarId,
        })
      );
      await this.findAll();
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao trocar a vaga do plano';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async salvarServicos(
    profissionalId: string,
    servicoIds: string[]
  ): Promise<void> {
    if (!servicoIds.length) {
      return;
    }

    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      await firstValueFrom(
        this.http.post<void>(`${this.baseUrl}/salvarServico`, {
          servicoId: servicoIds,
          profissionalId,
        })
      );

      await this.findAll();
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao vincular serviços ao profissional';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async removerServico(servicoProfissionalId: string): Promise<void> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      await firstValueFrom(
        this.http.delete<void>(`${this.baseUrl}/deleteServico`, {
          params: { id: servicoProfissionalId.toString() },
        })
      );

      await this.findAll();
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao remover serviço do profissional';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async buscarFechamento(
    profissionalId: string,
    dataInicio: string,
    dataFim: string
  ): Promise<{ agendamentos: any[]; total: number }> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      const resposta = await firstValueFrom(
        this.http.post<{ agendamentos: any[]; total: number }>(
          `${this.baseUrl}/fechamento`,
          {
            profissionalId,
            dataInicio,
            dataFim,
          }
        )
      );

      return resposta;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao buscar fechamento do profissional';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async buscarFechamentoGeral(
    estabelecimentoId: string,
    dataInicio: string,
    dataFim: string
  ): Promise<{
    porProfissional: {
      profissionalId: string;
      profissionalNome: string;
      agendamentos: any[];
      total: number;
    }[];
    totalGeral: number;
  }> {
    try {
      this.dataService.setLoading(true);
      this.dataService.limparErro();

      const resposta = await firstValueFrom(
        this.http.post<{
          porProfissional: {
            profissionalId: string;
            profissionalNome: string;
            agendamentos: any[];
            total: number;
          }[];
          totalGeral: number;
        }>(`${this.baseUrl}/fechamento-geral`, {
          estabelecimentoId,
          dataInicio,
          dataFim,
        })
      );

      return resposta;
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        'Erro ao buscar fechamento geral dos profissionais';
      this.dataService.setErro(mensagem);
      throw error;
    } finally {
      this.dataService.setLoading(false);
    }
  }

  async buscarMetricas(
    profissionalId: string
  ): Promise<{ cancelados: number; atendidos: number; confirmados: number }> {
    try {
      return await firstValueFrom(
        this.http.get<{
          cancelados: number;
          atendidos: number;
          confirmados: number;
        }>(`${this.baseUrl}/metricas`, { params: { id: profissionalId } })
      );
    } catch (error) {
      console.error('Erro ao buscar métricas do profissional', error);
      return { cancelados: 0, atendidos: 0, confirmados: 0 };
    }
  }

  async habilitarAcesso(
    profissionalId: string,
    email: string,
    telefone: string,
    senha: string
  ): Promise<void> {
    const telefoneLimpo = telefone.replace(/\D/g, '');
    await firstValueFrom(
      this.http.post<void>(
        `${this.baseUrl}/${profissionalId}/habilitar-acesso`,
        { email, telefone: telefoneLimpo, senha }
      )
    );
    await this.findAll();
  }

  async revogarAcesso(profissionalId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete<void>(`${this.baseUrl}/${profissionalId}/revogar-acesso`)
    );
    this.dataService.updateProfissional(profissionalId, { pessoaId: null });
    await this.findAll();
  }
}
